import { Plus, X, Clock, Calendar, CalendarRange, Globe } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { checkTimeCondition, normalizeTimeOfDay } from "@touchworks/shared/scheduling/scheduleWindows";

const DAYS = [
    { id: "mon", label: "S" },
    { id: "tue", label: "T" },
    { id: "wed", label: "Q" },
    { id: "thu", label: "Q" },
    { id: "fri", label: "S" },
    { id: "sat", label: "S" },
    { id: "sun", label: "D" },
];

const CONDITION_TYPES = [
    { id: 'time', label: 'Horário', icon: Clock },
    { id: 'weekdays', label: 'Dias da Semana', icon: Calendar },
    { id: 'date', label: 'Data/Período', icon: CalendarRange },
];

const MONTHS = [
    { v: '01', l: 'Jan' }, { v: '02', l: 'Fev' }, { v: '03', l: 'Mar' },
    { v: '04', l: 'Abr' }, { v: '05', l: 'Mai' }, { v: '06', l: 'Jun' },
    { v: '07', l: 'Jul' }, { v: '08', l: 'Ago' }, { v: '09', l: 'Set' },
    { v: '10', l: 'Out' }, { v: '11', l: 'Nov' }, { v: '12', l: 'Dez' }
];

const TIME_PROBLEMS = {
    invalid_start: 'Horário inicial inválido. Use HH:MM, ex.: 09:00',
    invalid_end: 'Horário final inválido. Use HH:MM, ex.: 18:00',
    end_not_after_start: 'O horário final deve ser depois do inicial',
};

const TIMEZONES = [
    { id: 'America/Sao_Paulo', label: 'Brasília (GMT-3)' },
    { id: 'America/Manaus', label: 'Manaus (GMT-4)' },
    { id: 'America/Rio_Branco', label: 'Rio Branco (GMT-5)' },
    { id: 'America/Noronha', label: 'Fernando de Noronha (GMT-2)' },
    { id: 'UTC', label: 'UTC (GMT+0)' },
];

// Componente CONTROLADO - estado vem do pai (PropertiesPanel)
export default function TimeRangePicker({ value = { windows: [] }, onChange }) {
    // Garante estrutura mínima
    const config = {
        windows: value?.windows || [],
        timezone: value?.timezone || 'America/Sao_Paulo'
    };

    const updateConfig = (newConfig) => {
        if (onChange) {
            onChange({ ...config, ...newConfig });
        }
    };

    // === WINDOW FUNCTIONS ===
    const addWindow = () => {
        updateConfig({
            windows: [...config.windows, { conditions: [] }]
        });
    };

    const removeWindow = (windowIdx) => {
        updateConfig({
            windows: config.windows.filter((_, i) => i !== windowIdx)
        });
    };

    // === CONDITION FUNCTIONS ===
    const addCondition = (windowIdx, type) => {
        const newCondition = { type };
        if (type === 'time') {
            newCondition.start = '09:00';
            newCondition.end = '18:00';
        } else if (type === 'weekdays') {
            newCondition.days = [];
        } else if (type === 'date') {
            newCondition.startDay = '';
            newCondition.startMonth = '';
            newCondition.startYear = '';
            newCondition.endDay = '';
            newCondition.endMonth = '';
            newCondition.endYear = '';
        }

        const newWindows = [...config.windows];
        newWindows[windowIdx] = {
            ...newWindows[windowIdx],
            conditions: [...(newWindows[windowIdx].conditions || []), newCondition]
        };
        updateConfig({ windows: newWindows });
    };

    const removeCondition = (windowIdx, condIdx) => {
        const newWindows = [...config.windows];
        newWindows[windowIdx] = {
            ...newWindows[windowIdx],
            conditions: newWindows[windowIdx].conditions.filter((_, i) => i !== condIdx)
        };
        updateConfig({ windows: newWindows });
    };

    const updateCondition = (windowIdx, condIdx, field, val) => {
        const newWindows = [...config.windows];
        const conditions = [...newWindows[windowIdx].conditions];
        conditions[condIdx] = { ...conditions[condIdx], [field]: val };
        newWindows[windowIdx] = { ...newWindows[windowIdx], conditions };
        updateConfig({ windows: newWindows });
    };

    // "1600", "16h" ou "9:00" viram "16:00" / "09:00" ao sair do campo; o que não
    // dá para ler fica como está e aparece em vermelho
    const normalizeTime = (windowIdx, condIdx, field, val) => {
        const normalized = normalizeTimeOfDay(val);
        if (normalized && normalized !== val) updateCondition(windowIdx, condIdx, field, normalized);
    };

    const toggleDay = (windowIdx, condIdx, dayId) => {
        const cond = config.windows[windowIdx].conditions[condIdx];
        const days = cond.days || [];
        const newDays = days.includes(dayId)
            ? days.filter(d => d !== dayId)
            : [...days, dayId];
        updateCondition(windowIdx, condIdx, 'days', newDays);
    };

    const hasConditionType = (windowIdx, type) => {
        return config.windows[windowIdx]?.conditions?.some(c => c.type === type);
    };

    // === RENDER CONDITION ===
    const renderCondition = (cond, windowIdx, condIdx) => {
        const condType = CONDITION_TYPES.find(t => t.id === cond.type);
        const Icon = condType?.icon || Clock;
        const timeProblem = cond.type === 'time' ? checkTimeCondition(cond) : null;

        return (
            <div key={condIdx} className="bg-surface/50 border border-border rounded-lg p-3 space-y-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-foreground-secondary">
                        <Icon className="w-4 h-4 text-pink-500" />
                        <span className="text-xs font-medium">{condType?.label}</span>
                    </div>
                    <button
                        onClick={() => removeCondition(windowIdx, condIdx)}
                        className="text-foreground-muted hover:text-red-400 transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {cond.type === 'time' && (
                    <div className="space-y-1">
                        <div className="flex items-center gap-3">
                            <div className="flex-1 space-y-1">
                                <Label className="text-[10px] text-foreground-muted">De</Label>
                                <Input
                                    type="text"
                                    placeholder="09:00"
                                    className="h-9 text-sm bg-background border-border text-center"
                                    value={cond.start || ''}
                                    aria-label="Horário inicial"
                                    onChange={(e) => updateCondition(windowIdx, condIdx, 'start', e.target.value)}
                                    onBlur={(e) => normalizeTime(windowIdx, condIdx, 'start', e.target.value)}
                                />
                            </div>
                            <span className="text-foreground-muted mt-5">até</span>
                            <div className="flex-1 space-y-1">
                                <Label className="text-[10px] text-foreground-muted">Até</Label>
                                <Input
                                    type="text"
                                    placeholder="18:00"
                                    className="h-9 text-sm bg-background border-border text-center"
                                    value={cond.end || ''}
                                    aria-label="Horário final"
                                    onChange={(e) => updateCondition(windowIdx, condIdx, 'end', e.target.value)}
                                    onBlur={(e) => normalizeTime(windowIdx, condIdx, 'end', e.target.value)}
                                />
                            </div>
                        </div>
                        {timeProblem && (
                            <p role="alert" className="text-[11px] text-red-400">
                                {TIME_PROBLEMS[timeProblem]}
                            </p>
                        )}
                    </div>
                )}

                {cond.type === 'weekdays' && (
                    <div className="flex flex-wrap gap-2">
                        {DAYS.map((day) => (
                            <div
                                key={day.id}
                                onClick={() => toggleDay(windowIdx, condIdx, day.id)}
                                className={`
                                    cursor-pointer text-xs w-8 h-8 flex items-center justify-center rounded-full border transition-all font-medium
                                    ${(cond.days || []).includes(day.id)
                                        ? "bg-pink-600 border-pink-500 text-white shadow-md shadow-pink-500/20"
                                        : "bg-background border-border text-foreground-muted hover:border-border-strong"
                                    }
                                `}
                            >
                                {day.label}
                            </div>
                        ))}
                    </div>
                )}

                {cond.type === 'date' && (
                    <div className="space-y-3">
                        <div className="space-y-1.5">
                            <Label className="text-[10px] text-foreground-muted">De (Início)</Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    type="text"
                                    placeholder="Dia"
                                    className="h-9 text-xs bg-background border-border w-14 text-center"
                                    value={cond.startDay || ''}
                                    onChange={(e) => updateCondition(windowIdx, condIdx, 'startDay', e.target.value)}
                                />
                                <span className="text-foreground-muted">/</span>
                                <Select
                                    value={cond.startMonth || ''}
                                    onValueChange={(val) => updateCondition(windowIdx, condIdx, 'startMonth', val)}
                                >
                                    <SelectTrigger className="h-9 text-xs bg-background border-border flex-1">
                                        <SelectValue placeholder="Mês" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[40vh] bg-background border-border">
                                        {MONTHS.map(m => (
                                            <SelectItem key={m.v} value={m.v} className="text-xs">{m.l}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <span className="text-foreground-muted">/</span>
                                <Input
                                    type="text"
                                    placeholder="Ano"
                                    className="h-9 text-xs bg-background border-border w-16 text-center"
                                    value={cond.startYear || ''}
                                    onChange={(e) => updateCondition(windowIdx, condIdx, 'startYear', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-[10px] text-foreground-muted">Até (Fim)</Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    type="text"
                                    placeholder="Dia"
                                    className="h-9 text-xs bg-background border-border w-14 text-center"
                                    value={cond.endDay || ''}
                                    onChange={(e) => updateCondition(windowIdx, condIdx, 'endDay', e.target.value)}
                                />
                                <span className="text-foreground-muted">/</span>
                                <Select
                                    value={cond.endMonth || ''}
                                    onValueChange={(val) => updateCondition(windowIdx, condIdx, 'endMonth', val)}
                                >
                                    <SelectTrigger className="h-9 text-xs bg-background border-border flex-1">
                                        <SelectValue placeholder="Mês" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[40vh] bg-background border-border">
                                        {MONTHS.map(m => (
                                            <SelectItem key={m.v} value={m.v} className="text-xs">{m.l}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <span className="text-foreground-muted">/</span>
                                <Input
                                    type="text"
                                    placeholder="Ano"
                                    className="h-9 text-xs bg-background border-border w-16 text-center"
                                    value={cond.endYear || ''}
                                    onChange={(e) => updateCondition(windowIdx, condIdx, 'endYear', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // === RENDER WINDOW ===
    const renderWindow = (window, windowIdx) => {
        return (
            <div key={windowIdx} className="space-y-3">
                {/* OR separator */}
                {windowIdx > 0 && (
                    <div className="flex items-center gap-2 py-2">
                        <div className="flex-1 h-px bg-surface-active" />
                        <span className="text-xs text-foreground-muted font-medium">ou</span>
                        <div className="flex-1 h-px bg-surface-active" />
                    </div>
                )}

                {/* Window card */}
                <div className="border border-border rounded-xl p-3 space-y-3 relative group">
                    {/* Remove window button */}
                    {config.windows.length > 1 && (
                        <button
                            onClick={() => removeWindow(windowIdx)}
                            className="absolute -top-2 -right-2 bg-surface border border-border-strong rounded-full p-1 text-foreground-muted hover:text-red-400 hover:border-red-400 transition-colors opacity-0 group-hover:opacity-100"
                        >
                            <X className="w-3 h-3" />
                        </button>
                    )}

                    {/* Summary line */}
                    <div className="text-[10px] text-pink-500 font-mono">
                        # Janela {windowIdx + 1}
                    </div>

                    {/* Conditions */}
                    {window.conditions?.length > 0 && (
                        <div className="space-y-2">
                            {window.conditions.map((cond, condIdx) => renderCondition(cond, windowIdx, condIdx))}
                        </div>
                    )}

                    {/* Add condition buttons */}
                    <div className="flex flex-wrap gap-2 pt-2">
                        {CONDITION_TYPES.map(type => (
                            <Button
                                key={type.id}
                                size="sm"
                                variant="outline"
                                disabled={hasConditionType(windowIdx, type.id)}
                                onClick={() => addCondition(windowIdx, type.id)}
                                className={`
                                    h-7 text-[10px] gap-1 border-dashed
                                    ${hasConditionType(windowIdx, type.id)
                                        ? 'opacity-30 cursor-not-allowed border-border text-foreground-muted'
                                        : 'border-border-strong text-foreground-muted hover:border-pink-500/50 hover:text-pink-400 hover:bg-pink-500/5'
                                    }
                                `}
                            >
                                <type.icon className="w-3 h-3" />
                                {type.label}
                            </Button>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-4">
            {/* Windows */}
            {config.windows.length > 0 ? (
                <div className="space-y-2">
                    {config.windows.map((window, idx) => renderWindow(window, idx))}
                </div>
            ) : (
                <p className="text-[11px] text-foreground-muted text-center py-3">
                    Adicione uma janela para definir quando o fluxo estará ativo.
                </p>
            )}

            {/* Add window button */}
            <Button
                variant="outline"
                size="sm"
                onClick={addWindow}
                className="w-full h-9 text-xs border-dashed border-border-strong text-foreground-muted hover:border-pink-500/50 hover:text-pink-400 hover:bg-pink-500/5"
            >
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Adicionar Janela de Horário
            </Button>

            {/* Timezone Selector */}
            <div className="space-y-2 pt-3 border-t border-border">
                <Label className="text-xs text-foreground-muted flex items-center gap-1.5">
                    <Globe className="w-3 h-3" />
                    Fuso Horário
                </Label>
                <Select
                    value={config.timezone}
                    onValueChange={(tz) => updateConfig({ timezone: tz })}
                >
                    <SelectTrigger className="h-9 text-xs bg-background border-border">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-background border-border">
                        {TIMEZONES.map(tz => (
                            <SelectItem key={tz.id} value={tz.id} className="text-xs">
                                {tz.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        </div>
    );
}
